// src/components/riskassessment/utils/cryptoCalculationUtils.js
//
// ARCrypto Risk Assessment — Scoring Engine
// Source: ARCrypto_Risk_Assessment_Blueprint.md (April 2026)
//
// Scoring Model:
//   1. Each question is scored 1–4.
//   2. Each dimension's average is normalized to 0–100.
//   3. Weighted composite: Tolerance 45% + Capacity 30% + Preference 25%.
//   4. Composite maps to one of 5 risk profiles.
//   5. Safety-rail overrides may CAP the profile to protect users.
//   6. Profile maps to a 7-vault allocation matrix (+ cash buffer).

/* ─── Backward-compat alias for the old "Ultra-Conservative" tier ──────── */
//
// The lowest tier was renamed from "Ultra-Conservative" to "Conservative"
// on 2026-06-02. Any saved user data — Firestore docs, in-flight result
// objects, third-party integrations — that references the old name needs
// to keep working. `normalizeProfileType` maps legacy strings to current
// keys before any lookup. Always call it on incoming profile names.
const LEGACY_PROFILE_ALIASES = {
  "Ultra-Conservative": "Conservative",
  // Collapsed 5 profiles → 3 on 2026-07-06 (student feedback). The two
  // top tiers fold into Aggressive. Any saved data referencing the old
  // names maps here before any lookup, so historical results still render.
  "Growth-Oriented": "Aggressive",
  "Ultra-Aggressive": "Aggressive",
};
export const normalizeProfileType = (name) =>
  LEGACY_PROFILE_ALIASES[name] || name;

/* ─── Dimension weights (must sum to 1.0) ─────────────────────────────── */
const DIMENSION_WEIGHTS = {
  tolerance: 0.45,
  capacity: 0.30,
  preference: 0.25,
};

/* ─── Profile thresholds (composite score → profile) ──────────────────── */
//
// Re-calibrated 2026-06-20 to fix a "scoring too aggressive" issue
// caused by even-spaced thresholds at 20/40/60/80/100. Under the
// even spacing, the LITERAL MIDDLE of the answer scale (avg 2.5 on
// a 1-4 slider, which normalizes to 50) was landing in
// Growth-Oriented — wrong, because a user who picks middle answers
// across the board should land in Moderate.
//
// New thresholds are conservative-leaning: a user has to clearly
// commit to aggressive answers (mostly 3s and 4s) to escape Moderate.
//
//   composite ≤ 35  → Conservative      (avg answer ≤ 2.05, mostly 1-2)
//   composite ≤ 62  → Moderate          (avg ≤ 2.86, centered around 2.5-3)
//   composite ≤ 80  → Growth-Oriented   (avg ≤ 3.40, committed 3s)
//   composite ≤ 93  → Aggressive        (avg ≤ 3.79, mostly 3s and 4s)
//   composite ≤ 100 → Ultra-Aggressive  (avg > 3.79, basically all 4s)
//
// Safety-rail overrides (Q3 ruin impact, Q8 emergency fund, etc.)
// remain in place — they can still cap a user below their score-derived
// bucket independent of these thresholds.
// Collapsed to 3 profiles 2026-07-06. Conservative + Moderate boundaries
// unchanged (keeps the 2026-06-20 re-calibration); everything above 62
// (former Growth-Oriented, Aggressive, Ultra-Aggressive) is now Aggressive.
const PROFILE_THRESHOLDS = [
  { max: 35, type: "Conservative" },
  { max: 62, type: "Moderate" },
  { max: 100, type: "Aggressive" },
];

/* ─── Profile rank for cap comparisons (lower = more conservative) ────── */
const PROFILE_RANK = {
  "Conservative": 0,
  Moderate: 1,
  Aggressive: 2,
};

/* ─── Vault Allocation Matrix (% per profile) ─────────────────────────── */
//   Source: blueprint table in section "Vault Allocation by Risk Profile"
//   Each row sums to 100. The 7 vaults + cash buffer.
const VAULT_ALLOCATIONS = {
  "Conservative": { leoBTC: 35, leoETH: 25, gemBTC: 15, gemini: 5, scorpioV: 0, gemETH: 0, gemSOL: 0, cash: 20 },
  Moderate:       { leoBTC: 20, leoETH: 15, gemBTC: 20, gemini: 15, scorpioV: 10, gemETH: 0, gemSOL: 0, cash: 20 },
  // Aggressive uses the FORMER "Growth-Oriented" allocation (the softer of
  // the three old aggressive-side mixes) — product decision 2026-07-06.
  Aggressive:     { leoBTC: 10, leoETH: 10, gemBTC: 15, gemini: 15, scorpioV: 20, gemETH: 10, gemSOL: 5, cash: 15 },
};

/* ─── Profile descriptions (concise — full prose in the blueprint) ────── */
const PROFILE_META = {
  "Conservative": {
    description:
      "Capital preservation above all. Cannot tolerate significant drawdowns. New to crypto or short time horizon.",
    recommendations: [
      "Anchor in Leo BTC Bull Run — sits in cash during uncertain markets, only deploys on confirmed trends",
      "Pair with Leo ETH for ETH momentum exposure with the same patient philosophy",
      "Maintain a 20% cash/stables buffer for psychological safety and rebalancing",
      "Expect drawdowns of -8% to -12% on individual vaults; blended portfolio typically lower",
    ],
    psychologicalInsights: [
      "You value certainty and stability — and that's a valid investment philosophy",
      "Volatility makes you uncomfortable; the Leo strategies are designed to sit out chaos",
      "Patience is your edge — boredom will be a feature, not a bug",
      "Avoid checking the portfolio daily; let the algorithms do their thing",
    ],
  },
  Moderate: {
    description:
      "Willing to accept controlled volatility for steady growth. Some experience, moderate financial capacity.",
    recommendations: [
      "Balance Leo vaults (anchor) with Gemini BTC's 28/28 recovery record for steady growth",
      "Add a 10% intro to Scorpio V for leveraged growth exposure without dominating risk",
      "Keep Gemini ETH and Gemini SOL excluded — their drawdowns exceed your capacity",
      "Maintain 20% cash buffer for stability and rebalancing",
    ],
    psychologicalInsights: [
      "You understand volatility is the price of growth — and you're willing to pay it within reason",
      "You don't panic when things dip but also don't enjoy wild swings",
      "Gemini BTC's controlled losses align with your temperament",
      "Stay disciplined: the small Scorpio V allocation is meaningful exposure without overdoing it",
    ],
  },
  Aggressive: {
    description:
      "Accepts meaningful drawdowns for above-average returns. Experienced, solid capacity, longer horizon.",
    recommendations: [
      "Make Scorpio V your primary growth engine — leveraged BTC trends with a +786% live track record",
      "Add Gemini ETH and a small Gemini SOL position for highest-return exposure",
      "Keep Leo BTC + Leo ETH as your conservative anchor",
      "Hold a cash buffer for tactical flexibility; expect -18% to -25% individual vault drawdowns",
    ],
    psychologicalInsights: [
      "You've been through at least one full crypto cycle and held conviction",
      "10–15% monthly drawdowns shouldn't shake you — you understand the recovery pattern",
      "Time horizon is your edge: 6–12 month windows let drawdown-recovery work in your favor",
      "Diversification across 7 vaults insulates you from any single bad month",
    ],
  },
};

/* ─── Safety Rail Override Rules ──────────────────────────────────────── */
//   Each rule examines specific answers and may CAP the user's profile.
//   The cap is applied only if the cap profile is MORE CONSERVATIVE than
//   the score-derived profile. Returns array of { rule, message, capTo }.
const evaluateSafetyRails = (answers) => {
  const overrides = [];

  // Rule 1: Q3 (ruin impact) ≤ 2 → cap at Moderate
  if (answers.q3 != null && answers.q3 <= 2) {
    overrides.push({
      rule: "Q3-ruin-impact",
      message:
        "Going to zero would be devastating or major hardship — capped at Moderate to protect financial stability. No exposure to Scorpio V, Gemini ETH, or Gemini SOL.",
      capTo: "Moderate",
    });
  }

  // Rule 2: Q8 (no emergency fund) = 1 → cap at Conservative
  if (answers.q8 != null && answers.q8 <= 1) {
    overrides.push({
      rule: "Q8-emergency-fund",
      message:
        "No emergency fund detected — capped at Conservative. Build a 6-month cash buffer before taking on meaningful crypto risk.",
      capTo: "Conservative",
    });
  }

  // Rule 3: Q6 (allocation) > 30% AND Q9 (employment) = unemployed → Conservative
  // Q6 = 3 means 30–50%, Q6 = 4 means >50%. Q9 = 1 means unemployed.
  if (answers.q6 != null && answers.q6 >= 3 && answers.q9 != null && answers.q9 <= 1) {
    overrides.push({
      rule: "Q6+Q9-concentration-income",
      message:
        "Crypto concentration exceeds 30% and no stable income — concentration risk + income risk = capped at Conservative.",
      capTo: "Conservative",
    });
  }

  // Rule 4: Q7 (time horizon) < 1 year → cap at Moderate
  if (answers.q7 != null && answers.q7 <= 1) {
    overrides.push({
      rule: "Q7-time-horizon",
      message:
        "Investment horizon is under 1 year — capped at Moderate. Short horizons cannot ride out drawdown-recovery cycles.",
      capTo: "Moderate",
    });
  }

  return overrides;
};

/* ─── Pick the most conservative cap from a set of overrides ──────────── */
const mostConservativeCap = (overrides) => {
  if (!overrides.length) return null;
  let lowestRank = Infinity;
  let cap = null;
  overrides.forEach((o) => {
    const rank = PROFILE_RANK[o.capTo];
    if (rank != null && rank < lowestRank) {
      lowestRank = rank;
      cap = o.capTo;
    }
  });
  return cap;
};

/* ─── Map composite score → profile type ──────────────────────────────── */
const scoreToProfile = (score) => {
  for (const t of PROFILE_THRESHOLDS) {
    if (score <= t.max) return t.type;
  }
  return PROFILE_THRESHOLDS[PROFILE_THRESHOLDS.length - 1].type;
};

/* ─── Calculate dimension scores (each 0–100) ─────────────────────────── */
const calculateDimensionScores = (answers, questions) => {
  const dimAccum = {
    tolerance: { sum: 0, count: 0 },
    capacity: { sum: 0, count: 0 },
    preference: { sum: 0, count: 0 },
  };

  questions.forEach((q) => {
    const dim = q.category;
    if (dimAccum[dim] && answers[q.id] != null) {
      dimAccum[dim].sum += answers[q.id];
      dimAccum[dim].count += 1;
    }
  });

  const out = {};
  Object.keys(dimAccum).forEach((dim) => {
    const { sum, count } = dimAccum[dim];
    // Normalize 1–4 average to 0–100 (1→0, 4→100)
    out[dim] = count > 0 ? ((sum / count - 1) / 3) * 100 : 0;
  });
  return out;
};

/* ─── Backward-compat psychological sub-profiles ──────────────────────── */
//   Existing UI components reference psychProfiles by sub-category id.
//   We keep this surface populated so the legacy components don't break.
export const calculatePsychologicalProfiles = (answers, questions) => {
  const psychQuestions = questions.filter((q) => q.category === "tolerance");
  const out = {};
  psychQuestions.forEach((q) => {
    if (answers[q.id] != null) {
      out[q.subCategory] = {
        raw: answers[q.id],
        percentage: ((answers[q.id] - 1) / 3) * 100,
        label: q.subCategory.replace(/_/g, " "),
      };
    }
  });
  return out;
};

/* ─── Mismatch warning helper (Q15 sold at loss + score Growth+) ──────── */
const checkBehaviorMismatch = (answers, finalProfileType) => {
  if (answers.q15 != null && answers.q15 <= 1) {
    const rank = PROFILE_RANK[finalProfileType];
    if (rank >= PROFILE_RANK["Aggressive"]) {
      return {
        warning:
          "Past behavior mismatch: you previously sold during a significant drawdown, but your composite score places you in a higher-risk profile. Past behavior is the strongest predictor of future behavior — consider whether your stated tolerance survives contact with real losses.",
      };
    }
  }
  return null;
};

/* ─── Build legacy portfolioAllocation shape from new vaultAllocation ─── */
//   Keeps existing UI components working until Phase 2 redesign.
const buildLegacyAllocation = (vaultAlloc) => ({
  stablecoins: vaultAlloc.cash,
  vaults: {
    total: 100 - vaultAlloc.cash,
    breakdown: {
      leo: vaultAlloc.leoBTC + vaultAlloc.leoETH,
      gemini:
        vaultAlloc.gemBTC + vaultAlloc.gemini + vaultAlloc.gemETH + vaultAlloc.gemSOL,
      scorpio: vaultAlloc.scorpioV,
    },
  },
});

/* ─── PUBLIC API: calculateResults ────────────────────────────────────── */
//
// Options:
//   honorStatedProfile (default false)
//     When true, safety-rail caps are NOT applied to the final profile.
//     The user's score-derived profile becomes canonical. Safety overrides
//     are still computed and returned in `safetyOverrides` so the UI can
//     show them as advisory banners instead of silent demotions.
//
//     Set per-user via the riskAssessmentOverride doc — see
//     src/services/riskAssessmentOverrideService.js. Default is to enforce
//     caps (protects students who haven't built emergency funds yet).
export const calculateResults = (answers, questions, options = {}) => {
  const { honorStatedProfile = false } = options;

  // 1. Dimension scores (each 0–100)
  const dimScores = calculateDimensionScores(answers, questions);

  // 2. Weighted composite score (0–100)
  const weightedScore =
    dimScores.tolerance * DIMENSION_WEIGHTS.tolerance +
    dimScores.capacity * DIMENSION_WEIGHTS.capacity +
    dimScores.preference * DIMENSION_WEIGHTS.preference;
  const composite = Math.round(weightedScore);

  // 3. Score-derived profile
  let profileType = scoreToProfile(composite);
  const scoreDerivedType = profileType;

  // 4. Compute safety-rail overrides — ALWAYS evaluated so the UI can
  //    surface them, but the cap is only applied when the user hasn't
  //    opted into honorStatedProfile.
  const overrides = evaluateSafetyRails(answers);
  const cap = mostConservativeCap(overrides);
  if (
    !honorStatedProfile &&
    cap &&
    PROFILE_RANK[cap] < PROFILE_RANK[profileType]
  ) {
    profileType = cap;
  }
  const capApplied = profileType !== scoreDerivedType;

  // 5. Behavior mismatch warning (separate from override)
  const behaviorWarning = checkBehaviorMismatch(answers, profileType);

  // 6. Look up vault allocation
  const vaultAllocation = VAULT_ALLOCATIONS[profileType];

  // 7. Build legacy-compatible portfolio allocation
  const portfolioAllocation = buildLegacyAllocation(vaultAllocation);

  // 8. Compose riskProfile object (existing UI shape)
  const meta = PROFILE_META[profileType];
  const riskProfile = {
    type: profileType,
    description: meta.description,
    recommendations: meta.recommendations,
    psychologicalInsights: meta.psychologicalInsights,
  };

  // 9. Backward-compat scores by old category names
  //    (existing components may key on 'psychological' / 'financial' / etc.)
  const legacyScores = {
    psychological: dimScores.tolerance,
    financial: dimScores.capacity,
    experience: 0, // no longer a separate dimension
    preference: dimScores.preference,
  };

  return {
    // ─── Legacy fields (preserved for existing UI components) ─────────
    totalScore: composite,
    scores: legacyScores,
    psychProfiles: calculatePsychologicalProfiles(answers, questions),
    riskProfile,
    portfolioAllocation,

    // ─── New fields for Phase 2 results page redesign ─────────────────
    composite: {
      tolerance: Math.round(dimScores.tolerance),
      capacity: Math.round(dimScores.capacity),
      preference: Math.round(dimScores.preference),
      weighted: composite,
      profile: profileType,
      scoreDerivedProfile: scoreDerivedType,
      capApplied,
    },
    vaultAllocation,
    safetyOverrides: overrides,
    behaviorWarning,
    weights: DIMENSION_WEIGHTS,
  };
};

const utils = {
  calculatePsychologicalProfiles,
  calculateResults,
};

export default utils;
